import L from "leaflet";
import wjfIcon from "../assets/icons/wjf-logo.png";
import { 
  generateCategoryIcon, 
  generateSelectedIcon, 
  generateImgIcon 
} from "./canvasIcons";

let initialised = false;

// Initialize static icons
const icons = {
  // selectedIcon is now generated dynamically via getSelectedIcon() for theme awareness
  // Keep userIcon as DOM-based to maintain CSS variable color and pulsing animation
  userIcon: L.divIcon({
    html: `
    <div
      class="pulsing-dot"
      style="position:relative;z-index:100;background-color:var(--inyc-secondary);display:flex;justify-items:center;align-items:center;width:12px;height:12px;box-shadow:0px 0px 13px rgba(0,0,0,0.7); border-radius:50%; display:flex; justify-content:center; align-items:center;"
    >
    </div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    className: "border-none bg-transparent",
  }),
};

// for (const category of categories) {
//   icons[category.id] = L.divIcon({
//     html: `<div style="width:20px; height:20px; border-radius:50%;display:flex;justify-content:center;align-items:center;transform:translate(-25%,-25%)">
//       <div
//         style="background-color:${category.activeColor}; width:10px; height:10px; border-radius:50%;"
//       >
//       </div>
//     </div>`,
//     iconSize: [10, 10],
//     iconAnchor: [5, 5], // to center the icon
//     className: "border-none bg-transparent",
//   });
// }

export function createCategoryIcons(categories) {
  console.log("initialising icons with categories (canvas-based)", categories);

  for (const category of categories) {
    // Generate normal-sized icons with event counts (1-6)
    for (let i = 1; i <= 6; i++) {
      const iconKey = `${category.id}${i}`;
      const dataUrl = generateCategoryIcon(category.activeColor, i, false);
      
      icons[iconKey] = L.icon({
        iconUrl: dataUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 10], // Center the icon
        className: "border-none bg-transparent",
      });
    }

    // Generate small version of the icon (for zoomed out view)
    const smallIconKey = `${category.id}small`;
    const smallDataUrl = generateCategoryIcon(category.activeColor, 1, true);
    
    icons[smallIconKey] = L.icon({
      iconUrl: smallDataUrl,
      iconSize: [8, 8],
      iconAnchor: [4, 4], // Center the icon
      className: "border-none bg-transparent",
    });
  }
  
  initialised = true;
  console.log(`✅ Generated ${Object.keys(icons).length} canvas-based icons`);
}

// Cache for generated image icons to avoid re-generating
const imgIconCache = new Map();

// Event emitter for icon ready notifications
const iconReadyListeners = new Set();
export const onIconReady = (callback) => {
  iconReadyListeners.add(callback);
  return () => iconReadyListeners.delete(callback);
};
const notifyIconReady = (cacheKey) => {
  iconReadyListeners.forEach(cb => cb(cacheKey));
};

// Track pending icon generations to avoid duplicate work
const pendingIcons = new Set();

export const createImgIcon = (img, numberOfEvents = 1) => {
  // Create a cache key
  const cacheKey = `${img}_${numberOfEvents}`;
  
  // Check cache first - return cached icon immediately
  if (imgIconCache.has(cacheKey)) {
    return imgIconCache.get(cacheKey);
  }
  
  // Create a temporary icon while generating
  const tempIcon = L.icon({
    iconUrl: generateCategoryIcon('#666', numberOfEvents, false), // Gray placeholder
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    className: "border-none bg-transparent",
  });
  
  // Only start generation if not already pending
  if (!pendingIcons.has(cacheKey)) {
    pendingIcons.add(cacheKey);
    
    // Generate the actual icon asynchronously
    generateImgIcon(img, numberOfEvents).then(dataUrl => {
      const icon = L.icon({
        iconUrl: dataUrl,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        className: "border-none bg-transparent",
      });
      imgIconCache.set(cacheKey, icon);
      pendingIcons.delete(cacheKey);
      // Notify listeners that icon is ready
      notifyIconReady(cacheKey);
    }).catch(err => {
      console.error('Failed to generate image icon:', err);
      pendingIcons.delete(cacheKey);
    });
  }
  
  return tempIcon;
};

export function createCategoryIcon(
  categoryId,
  overridecolor,
  numberOfEvents,
  small = false
) {
  if (!initialised) {
    const getIcon = () => {
      if (initialised) {
        return small
          ? icons[`${categoryId}small`] || icons["music1small"]
          : icons[
              `${categoryId}${numberOfEvents > 6 ? "6" : numberOfEvents || 1}`
            ] || icons["music1"];
      } else {
        setTimeout(getIcon, 1000);
      }
    };
    getIcon();
  }

  if (overridecolor) {
    // Generate selected icon dynamically for theme awareness
    return L.icon({
      iconUrl: generateSelectedIcon(),
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      className: "border-none bg-transparent",
    });
  }

  return small
    ? icons[`${categoryId}small`] || icons["music1small"]
    : icons[
        `${categoryId}${numberOfEvents > 6 ? "6" : numberOfEvents || 1}`
      ] || icons["music1"];
}


export function createUserIcon() {
  return icons.userIcon;
}

const venueIcons = {
  default: L.divIcon({
    html: `
    <div
      style="width:14px;height:14px;border-radius:50%;background:var(--inyc-map-venue-pin);border:2px solid var(--inyc-map-venue-pin-ring);box-shadow:0 0 8px color-mix(in srgb, var(--inyc-map-venue-pin) 45%, transparent);"
    ></div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    className: "border-none bg-transparent",
  }),
  selected: L.divIcon({
    html: `
    <div
      style="width:16px;height:16px;border-radius:50%;background:var(--inyc-map-venue-pin);border:2px solid var(--inyc-map-venue-pin-ring);box-shadow:0 0 10px color-mix(in srgb, var(--inyc-map-venue-pin) 70%, transparent);"
    ></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    className: "border-none bg-transparent",
  }),
};

export function createVenueIcon(selected = false) {
  return selected ? venueIcons.selected : venueIcons.default;
}

export const createIconFromImage = (image, size, anchor) => {
  return L.icon({
    iconUrl: image,
    // shadowUrl: markerShadow,
    // shadowAnchor: [15, 40],
    iconSize: size || [15, 15],
    iconAnchor: anchor || [8.1, 9],
  });
};
